import React from "react";
import { TextField, MenuItem, FormHelperText, Box, alpha } from "@mui/material";

const DynamicSelectInput = ({
  options,
  name,
  selectedValue,
  label,
  onChange,
  initialLabel = false,
  labelKey = "name",
  flex = false,
  required = false,
  error = false,
  helperText = "",
  disabled = false,
  placeholder = "",
  size = "small",
  fullWidth = true,
  width = "100%",
  ...props
}) => {
  const getDisplayValue = () => {
    if (!selectedValue) return "";
    const selected = options?.find(item => item.id === selectedValue);
    return selected ? selected[labelKey] : "";
  };

  return (
    <Box sx={{ 
      width: fullWidth ? "100%" : width,
      ...(flex && { flex: 1 }),
    }}>
      <TextField
        id={name}
        select
        name={name}
        value={selectedValue || ""}
        onChange={onChange}
        required={required}
        error={error}
        disabled={disabled}
        variant="outlined"
        fullWidth
        size={size}
        placeholder={placeholder}
        label={label}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: "8px",
            backgroundColor: disabled ? "#f1f5f9" : "#ffffff",
            transition: "all 0.2s ease",
            '& fieldset': {
              borderColor: error ? "#dc2626" : "#e2e8f0",
              borderWidth: error ? "2px" : "1.5px",
            },
            '&:hover fieldset': {
              borderColor: error ? "#dc2626" : "#94a3b8",
              borderWidth: error ? "2px" : "1.5px",
            },
            '&.Mui-focused fieldset': {
              borderColor: error ? "#dc2626" : "#64748b",
              borderWidth: "2px",
              boxShadow: error 
                ? "0 0 0 3px rgba(220, 38, 38, 0.1)" 
                : "0 0 0 3px rgba(100, 116, 139, 0.1)",
            },
          },
          '& .MuiSelect-select': {
            padding: "8px 14px",
            fontSize: "0.875rem",
            fontWeight: 500,
            color: selectedValue ? "#1e293b" : "#94a3b8",
            minHeight: "24px",
            display: "flex",
            alignItems: "center",
          },
          '& .MuiInputLabel-root': {
            fontSize: "0.85rem",
            color: "#64748b",
            fontWeight: 500,
            '&.Mui-focused': {
              color: "#475569",
            },
            '&.Mui-error': {
              color: "#dc2626",
            },
          },
          '& .MuiSelect-icon': {
            color: error ? "#dc2626" : "#94a3b8",
            transition: "transform 0.2s ease",
          },
          '& .MuiSelect-iconOpen': {
            transform: "rotate(180deg)",
          },
        }}
        SelectProps={{
          displayEmpty: true,
          renderValue: (value) => {
            if (!value) {
              return <span style={{ color: "#94a3b8", fontWeight: 400 }}>
                {placeholder || `Select ${label || name}`}
              </span>;
            }
            return getDisplayValue() || value;
          },
          MenuProps: {
            PaperProps: {
              sx: {
                borderRadius: "8px",
                marginTop: "4px",
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0)",
                border: "1px solid #e2e8f0",
                overflow: "hidden",
                maxHeight: "300px",
                '& .MuiMenuItem-root': {
                  fontSize: "0.875rem",
                  padding: "10px 16px",
                  fontWeight: 400,
                  color: "#1e293b",
                  transition: "all 0.15s ease",
                  borderBottom: "1px solid #f1f5f9",
                  '&:last-child': {
                    borderBottom: "none",
                  },
                  '&:hover': {
                    backgroundColor: "#f1f5f9",
                  },
                  '&.Mui-selected': {
                    backgroundColor: "#e2e8f0",
                    color: "#0f172a",
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: "#d1d5db",
                    },
                  },
                  '&.Mui-focusVisible': {
                    backgroundColor: "#f1f5f9",
                  },
                },
              },
            },
          },
        }}
        {...props}
      >
        {initialLabel && (
          <MenuItem value="">
            <em style={{ color: "#94a3b8", fontStyle: "italic" }}>
              {placeholder || `Select ${label || name}`}
            </em>
          </MenuItem>
        )}
        {options?.map((item) => (
          <MenuItem key={item.id} value={item.id}>
            {item[labelKey]}
          </MenuItem>
        ))}
      </TextField>
      
      {error && helperText && (
        <FormHelperText 
          error 
          sx={{ 
            mt: 0.5, 
            fontSize: "0.75rem",
            fontWeight: 500,
            color: "#dc2626",
          }}
        >
          {helperText}
        </FormHelperText>
      )}
      {!error && helperText && (
        <FormHelperText 
          sx={{ 
            mt: 0.5, 
            fontSize: "0.7rem",
            color: "#94a3b8",
          }}
        >
          {helperText}
        </FormHelperText>
      )}
    </Box>
  );
};

export default DynamicSelectInput;